"use client";

import React, {useEffect, useRef, useState} from "react";
import {X} from "lucide-react";
import {HorizontalTab} from "@/shared/components/HorizontalTab";
import {autoTableColumnLabels} from "@/features/i18n/autoTableColumns";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {Button} from "@/shared/components/ui/button";
import {AutoDetailsVehicleTab} from "./AutoDetailsTabs/AutoDetailsVehicleTab";
import {AutoDetailsOptionsTab} from "./AutoDetailsTabs/AutoDetailsOptionsTab";
import {AutoDetailsSellingTab} from "./AutoDetailsTabs/AutoDetailsSellingTab";
import {getAutoDetailsDisplayValue} from "./autoDetailsUtils";

export interface AutoDetailsModalProps {
    data: AutoTableInterface;
    onClose: () => void;
}

const tabs = [
    {id: "vehicle", label: "Pojazd"},
    {id: "options", label: "Opcje"},
    {id: "documents", label: "Dokumenty"},
    {id: "sales", label: "Sprzedaż"},
    {id: "delivery", label: "Wydanie"},
];

const tabContent: Record<string, string> = {
    vehicle: "Podstawowe informacje o pojeździe i jego stanie.",
    options: "Podgląd kluczowych opcji oraz wyposażenia.",
    documents: "Miejsce na dokumenty i pliki do pobrania.",
    sales: "Podsumowanie statusu sprzedaży i transakcji.",
    delivery: "Informacje o wydaniu pojazdu i statusie odbioru.",
};

const summaryFieldKeys: Array<keyof AutoTableInterface> = [
    "f_ord_num",
    "f_ord_d",
    "f_vin",
    "f_supp_2_status_desc",
];

export const AutoDetailsModal = ({data, onClose}: AutoDetailsModalProps) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState("vehicle");

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 md:p-6">
            <div
                ref={modalRef}
                className="flex h-[85vh] w-[85vw] flex-col rounded-lg bg-white shadow-lg"
            >
                <div className="flex items-center justify-between border-b px-5 py-4">
                    <h2 className="text-lg font-semibold">Dane pojazdu</h2>
                    <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <X className="h-5 w-5"/>
                    </Button>
                </div>

                <div className="flex-1 overflow-hidden">
                    <div className="h-full overflow-y-auto">
                        <div className="sticky top-0 z-10 bg-white px-5 pt-4 pb-4">
                            <div
                                className="flex w-full flex-wrap gap-x-2 md:gap-x-4 lg:gap-x-6 gap-y-2 text-sm md:flex-nowrap">
                                {summaryFieldKeys.map((key) => (
                                    <div key={key} className="flex flex-shrink-0 items-center gap-2 text-left">
                                        <span className="text-muted-foreground">
                                            {autoTableColumnLabels[key] ?? key}
                                        </span>
                                        <span className="font-medium text-foreground">
                                            {getAutoDetailsDisplayValue(data, key)}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4">
                                <HorizontalTab
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    onTabChange={setActiveTab}
                                />
                            </div>

                            <div className="mt-4 h-px w-full bg-border"/>
                        </div>

                        <div className="px-5 pb-4 pt-4">
                            {activeTab === "vehicle" && <AutoDetailsVehicleTab data={data} />}
                            {activeTab === "options" && <AutoDetailsOptionsTab data={data} />}
                            {activeTab === "sales" && <AutoDetailsSellingTab data={data} />}
                            {activeTab !== "vehicle" && activeTab !== "options" && activeTab !== "sales" && (
                                <div className="text-left text-sm text-muted-foreground">
                                    {tabContent[activeTab]}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
